import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Alert,
  CircularProgress,
  Typography,
  Divider,
  Chip,
  Box,
  Link,
} from '@mui/material';

import {
  useConfirmarPago,
  useRechazarPago,
  useComprobanteUrl,
} from '../../hooks/usePagos';
import { useAuth } from '../../context/AuthContext';
import {
  type Pago,
  getEstadoPagoLabel,
  getEstadoPagoColor,
  getViaPagoLabel,
  requiereComprobante,
} from '../../types/pago';
import { formatCurrency, formatDate, formatDateTime } from '../../utils/format';
import { mapSupabaseError } from '../../utils/errores';

type Modo = 'ver' | 'confirmar' | 'rechazar';

interface Props {
  open: boolean;
  pago: Pago;
  modo: Modo;
  onClose: () => void;
}

export default function AccionPagoDialog({ open, pago, modo, onClose }: Props) {
  const { profile } = useAuth();
  const confirmar = useConfirmarPago();
  const rechazar = useRechazarPago();
  const { data: signedUrl } = useComprobanteUrl(pago.comprobante_url);

  const [motivo, setMotivo] = useState('');
  const [motivoError, setMotivoError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setMotivo('');
      setMotivoError(null);
    }
  }, [open, pago.id]);

  const comprobanteObligatorio = requiereComprobante(pago.via_pago);
  const faltaComprobante =
    comprobanteObligatorio && !pago.comprobante_url;

  const handleConfirmar = async () => {
    if (!profile) return;
    try {
      await confirmar.mutateAsync({ id: pago.id, userId: profile.id });
      onClose();
    } catch {
      // se muestra abajo
    }
  };

  const handleRechazar = async () => {
    if (!profile) return;
    const m = motivo.trim();
    // Min 5 chars: el backend (RPC rechazar_transaccion / rechazar_pago) exige
    // mínimo 5 — alineamos UI para no recibir error del server al submitir (#031).
    if (m.length < 5) {
      setMotivoError('Indicá un motivo (mínimo 5 caracteres).');
      return;
    }
    if (m.length > 500) {
      setMotivoError('El motivo no puede superar 500 caracteres.');
      return;
    }
    try {
      await rechazar.mutateAsync({ id: pago.id, userId: profile.id, motivo: m });
      onClose();
    } catch {
      // se muestra abajo
    }
  };

  const apiError = (confirmar.error || rechazar.error) as Error | null;

  // Error crudo a consola para diagnóstico; al usuario, mensaje traducido (#490).
  useEffect(() => {
    if (apiError) {
      // eslint-disable-next-line no-console
      console.error('[AccionPago] error al procesar el pago:', apiError);
    }
  }, [apiError]);

  const title =
    modo === 'confirmar'
      ? 'Confirmar acreditación del pago'
      : modo === 'rechazar'
        ? 'Rechazar pago'
        : 'Detalle del pago';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {apiError && (
            <Alert severity="error">
              {mapSupabaseError(apiError, 'No se pudo procesar el pago. Volvé a intentar; si persiste, avisá al administrador.')}
            </Alert>
          )}

          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="body2" color="text.secondary">Cupón</Typography>
              <Typography variant="body2" fontFamily="monospace">
                {pago.cupon?.codigo ?? '—'}
              </Typography>
            </Box>
            <Chip
              label={getEstadoPagoLabel(pago.estado)}
              color={getEstadoPagoColor(pago.estado)}
              size="small"
            />
          </Stack>

          <Divider />

          <Stack spacing={1}>
            <Row label="Empresa" value={pago.empresa?.razon_social ?? '—'} />
            <Row label="Período" value={pago.periodo?.periodo ?? '—'} />
            <Row label="Vía" value={getViaPagoLabel(pago.via_pago)} />
            <Row label="Monto pagado" value={formatCurrency(pago.monto_pagado)} bold />
            <Row label="Monto del cupón" value={formatCurrency(pago.monto_total)} />
            <Row label="Diferencia" value={formatCurrency(pago.diferencia)} />
            <Row label="Fecha de pago" value={formatDate(pago.fecha_pago)} />
            {pago.nro_comprobante && (
              <Row label="N° comprobante" value={pago.nro_comprobante} />
            )}
            {pago.medio_pago && (
              <Row label="Medio" value={pago.medio_pago} />
            )}
            {pago.observaciones && (
              <Row label="Observaciones" value={pago.observaciones} />
            )}
          </Stack>

          <Divider />

          {/* Comprobante */}
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Comprobante
            </Typography>
            {pago.comprobante_url ? (
              <Link href={signedUrl ?? '#'} target="_blank" rel="noopener noreferrer">
                Abrir comprobante adjunto
              </Link>
            ) : (
              <Typography variant="body2">
                Sin comprobante adjunto
                {comprobanteObligatorio && (
                  <Typography
                    component="span"
                    variant="caption"
                    color="error"
                    sx={{ ml: 1 }}
                  >
                    (obligatorio para acreditar)
                  </Typography>
                )}
              </Typography>
            )}
          </Box>

          {/* Pago informado por la empresa: ATSA debe verificarlo antes de acreditar */}
          {pago.informado_por_empresa && pago.estado === 'pendiente' && (
            <Alert severity="info">
              Pago <strong>informado por la empresa</strong>. Verificá en el extracto
              bancario que la transferencia haya ingresado y que coincida el monto
              antes de confirmar.
            </Alert>
          )}

          {/* Estado de auditoría si ya fue confirmado/rechazado */}
          {pago.estado === 'acreditado' && pago.fecha_confirmacion && (
            <Alert severity="success">
              Acreditado el {formatDateTime(pago.fecha_confirmacion)}
            </Alert>
          )}
          {pago.estado === 'rechazado' && (
            <Alert severity="error">
              <Typography variant="body2">
                Rechazado el {formatDateTime(pago.fecha_rechazo)}
              </Typography>
              {pago.motivo_rechazo && (
                <Typography variant="body2">
                  Motivo: <em>{pago.motivo_rechazo}</em>
                </Typography>
              )}
            </Alert>
          )}

          {/* Inputs específicos según modo */}
          {modo === 'confirmar' && faltaComprobante && (
            <Alert severity="warning">
              No hay comprobante adjunto. La acreditación será rechazada por la
              base de datos. Subí el comprobante desde el detalle del pago antes
              de confirmar.
            </Alert>
          )}

          {modo === 'rechazar' && (
            <TextField
              label="Motivo del rechazo"
              required
              fullWidth
              multiline
              rows={3}
              value={motivo}
              onChange={(e) => {
                setMotivo(e.target.value);
                setMotivoError(null);
              }}
              error={!!motivoError}
              helperText={
                motivoError ?? `Mínimo 5 caracteres · ${motivo.trim().length}/500. Se muestra al empresario.`
              }
              inputProps={{ maxLength: 500 }}
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={confirmar.isPending || rechazar.isPending}>
          {modo === 'ver' ? 'Cerrar' : 'Cancelar'}
        </Button>

        {modo === 'confirmar' && (
          <Button
            variant="contained"
            color="success"
            disabled={confirmar.isPending || faltaComprobante}
            onClick={handleConfirmar}
            startIcon={
              confirmar.isPending ? <CircularProgress size={18} color="inherit" /> : null
            }
          >
            {confirmar.isPending ? 'Confirmando...' : 'Confirmar acreditación'}
          </Button>
        )}

        {modo === 'rechazar' && (
          <Button
            variant="contained"
            color="error"
            disabled={rechazar.isPending}
            onClick={handleRechazar}
            startIcon={
              rechazar.isPending ? <CircularProgress size={18} color="inherit" /> : null
            }
          >
            {rechazar.isPending ? 'Rechazando...' : 'Rechazar pago'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

function Row({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <Stack direction="row" justifyContent="space-between">
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="body2" fontWeight={bold ? 600 : 400}>{value}</Typography>
    </Stack>
  );
}
